import axios from 'axios';
import { API_BASE_URL } from '../config/constants';

// Create an axios instance for accounting API calls
const accountingAPI = axios.create({
  baseURL: `${API_BASE_URL}/accounting/`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include authentication token
accountingAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // Don't override Content-Type if it's already set in the request config
    // This is important for FormData uploads which need 'multipart/form-data'
    if (config.headers['Content-Type'] === 'multipart/form-data') {
      // For multipart/form-data, let the browser set the correct boundary
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Partners API
export const getAllPartners = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    
    const response = await accountingAPI.get(`partners/?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching partners:', error);
    throw error;
  }
};

// Products API
export const getAllProducts = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    
    const response = await accountingAPI.get(`products/?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

// Payment Terms API
export const getAllPaymentTerms = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    
    const response = await accountingAPI.get(`payment-terms/?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching payment terms:', error);
    throw error;
  }
};

// Categories API
export const getAllCategories = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    
    const response = await accountingAPI.get(`categories/?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

// Categories Tree API
export const getCategoryTree = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    
    const response = await accountingAPI.get(`categories/tree/?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching category tree:', error);
    throw error;
  }
};

// Banks API
export const getAllBanks = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    
    const response = await accountingAPI.get(`banks/?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching banks:', error);
    throw error;
  }
};

// Invoice Items API
export const getAllInvoiceItems = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    
    const response = await accountingAPI.get(`invoice-items/?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching invoice items:', error);
    throw error;
  }
};

// Invoices API
export const getAllInvoices = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    
    const response = await accountingAPI.get(`invoices/?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching invoices:', error);
    throw error;
  }
};

// Create Invoice API
export const createInvoice = async (invoiceData) => {
  try {
    const response = await accountingAPI.post('invoices/', invoiceData);
    return response.data;
  } catch (error) {
    console.error('Error creating invoice:', error);
    throw error;
  }
};

// Statements API
export const getAllStatements = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    
    const response = await accountingAPI.get(`statements/?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching statements:', error);
    throw error;
  }
};

// Create Statement API
export const createStatement = async (statementData, fileObj) => {
  try {
    // Use FormData if a file is included
    if (fileObj && fileObj.originFileObj) {
      const formData = new FormData();
      
      // Add file to form data
      formData.append('file', fileObj.originFileObj);
      
      // Add other statement data as JSON string in a field called 'data'
      formData.append('data', JSON.stringify(statementData));
      
      // Use multipart/form-data content type for file upload
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };
      
      const response = await accountingAPI.post('statements/', formData, config);
      return response.data;
    } else {
      // If no file, use regular JSON submission
      const response = await accountingAPI.post('statements/', statementData);
      return response.data;
    }
  } catch (error) {
    console.error('Error creating statement:', error);
    throw error;
  }
};

// Get Statement By ID API
export const getStatementById = async (id) => {
  try {
    const response = await accountingAPI.get(`statements/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching statement with ID ${id}:`, error);
    throw error;
  }
};

// Update Statement API
export const updateStatement = async (id, statementData, fileObj) => {
  try {
    // Use FormData if a file is included
    if (fileObj && fileObj.originFileObj) {
      const formData = new FormData();
      
      // Add file to form data
      formData.append('file', fileObj.originFileObj);
      
      // Add other statement data as JSON string in a field called 'data'
      formData.append('data', JSON.stringify(statementData));
      
      // Use multipart/form-data content type for file upload
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };
      
      const response = await accountingAPI.put(`statements/${id}/`, formData, config);
      return response.data;
    } else {
      // If no file, use regular JSON submission
      const response = await accountingAPI.put(`statements/${id}/`, statementData);
      return response.data;
    }
  } catch (error) {
    console.error(`Error updating statement with ID ${id}:`, error);
    throw error;
  }
};

// Create Category API
export const createCategory = async (categoryData) => {
  try {
    const response = await accountingAPI.post('categories/', categoryData);
    return response.data;
  } catch (error) {
    console.error('Error creating category:', error);
    throw error;
  }
};

// Update Category API
export const updateCategory = async (id, categoryData) => {
  try {
    const response = await accountingAPI.put(`categories/${id}/`, categoryData);
    return response.data;
  } catch (error) {
    console.error(`Error updating category with ID ${id}:`, error);
    throw error;
  }
};

// Delete Category API
export const deleteCategory = async (id) => {
  try {
    const response = await accountingAPI.delete(`categories/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting category with ID ${id}:`, error);
    throw error;
  }
};

// Get Invoice History API
export const getInvoiceHistory = async (invoiceId) => {
  try {
    const response = await accountingAPI.get(`invoices/${invoiceId}/history/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching history for invoice with ID ${invoiceId}:`, error);
    throw error;
  }
};

// Get All Invoice History API
export const getAllInvoiceHistory = async (brandIds = [], page = 1, pageSize = 25) => {
  // Construct brand filter params
  const brandParams = brandIds.map(id => `brand=${id}`).join('&');
  const params = `${brandParams}${brandParams ? '&' : ''}page=${page}&page_size=${pageSize}`;
  
  const response = await accountingAPI.get(`invoices/all-history/?${params}`);
  return response.data;
};

export const rollbackInvoiceHistory = async (historyId) => {
  try {
    const response = await accountingAPI.post('invoices/rollback-history/', {
      history_id: historyId
    });
    return response.data;
  } catch (error) {
    console.error(`Error rolling back invoice history with ID ${historyId}:`, error);
    throw error;
  }
};

// Get All Statement History API
export const getAllStatementHistory = async (brandIds = [], page = 1, pageSize = 25) => {
  try {
    const params = new URLSearchParams();
    
    // Handle brand IDs as array
    if (brandIds && brandIds.length > 0) {
      brandIds.forEach(id => params.append('brand', id));
    }
    
    // Add pagination parameters
    params.append('page', page);
    params.append('page_size', pageSize);
    
    const response = await accountingAPI.get(`statements/all-history/?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching all statement history:', error);
    throw error;
  }
};

// Rollback Statement to a previous history state
export const rollbackStatementHistory = async (historyId) => {
  try {
    const response = await accountingAPI.post('statements/rollback-history/', {
      history_id: historyId
    });
    return response.data;
  } catch (error) {
    console.error(`Error rolling back statement history with ID ${historyId}:`, error);
    throw error;
  }
};

// Get Invoices by Brands API
export const getInvoicesByBrands = async (brandIds = []) => {
  try {
    const params = new URLSearchParams();
    
    // Handle brand IDs as array
    if (brandIds && brandIds.length > 0) {
      brandIds.forEach(id => params.append('brand', id));
    }
    
    const response = await accountingAPI.get(`invoices/?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching invoices by brands:', error);
    throw error;
  }
};
